import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type SubscriptionBadgeStatus = "active" | "expiring" | "expired" | "none";

export interface SubscriptionStatusInfo {
  status: SubscriptionBadgeStatus;
  daysRemaining: number | null;
  planName: string | null;
  endsAt: string | null;
}

/**
 * Returns the user's most recent subscription with a derived badge status.
 * - active: status='active' and end date in the future (>7 days)
 * - expiring: active and ≤7 days remaining
 * - expired: latest subscription is past end date / canceled / status!=active
 * - none: no subscription record at all
 */
export const useSubscriptionStatus = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["subscription-status-badge", user?.id],
    queryFn: async (): Promise<SubscriptionStatusInfo> => {
      if (!user) return { status: "none", daysRemaining: null, planName: null, endsAt: null };
      const { data, error } = await supabase
        .from("subscriptions")
        .select("status, plan_name, current_period_end, canceled_at, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = data?.[0];
      if (!row) return { status: "none", daysRemaining: null, planName: null, endsAt: null };

      const end = row.current_period_end ? new Date(row.current_period_end) : null;
      const now = new Date();
      const daysRemaining = end
        ? Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86_400_000))
        : null;

      const isActive = row.status === "active" && (!end || end.getTime() > now.getTime());
      let status: SubscriptionBadgeStatus = "expired";
      if (isActive) {
        status = daysRemaining !== null && daysRemaining <= 7 ? "expiring" : "active";
      }
      return {
        status,
        daysRemaining,
        planName: row.plan_name ?? null,
        endsAt: row.current_period_end ?? null,
      };
    },
    enabled: !!user,
    staleTime: 60_000,
  });
};
